import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Minus, X, Layers } from "lucide-react";
import { formatDOP } from "@/lib/format";

/**
 * CartItem — Fila de un item en el carrito del POS.
 *
 * Extraído de POS.jsx para mantener el archivo bajo control y evitar la advertencia
 * de ESLint "unstable-nested-components". Es un componente puramente controlado:
 * todas las decisiones de estado ocurren arriba en POS.jsx.
 */
export default function CartItem({
  item,
  qtyDraft,
  priceDraft,
  canDiscount,
  onChangeUnit,
  onQtyDraftChange,
  onQtyCommit,
  onPriceDraftChange,
  onPriceCommit,
  onUpdateQty,
  onRemove,
}) {
  const key = item._cart_key || item.product_id;
  const qtyValue = qtyDraft !== undefined ? qtyDraft : item.quantity;
  const priceValue = priceDraft !== undefined ? priceDraft : item.price;
  const priceEdited = item.list_price && item.price < item.list_price - 0.005;
  const hasSaleUnits = Array.isArray(item.sale_units) && item.sale_units.length > 0;
  const currentUnit = item.sale_unit || "__BASE__";

  return (
    <div className="p-3 border-b border-border/60 hover:bg-accent/30 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] data-cell text-muted-foreground">{item.sku}</div>
          <div className="text-sm font-semibold truncate" title={item.name}>
            {item.base_name || item.name}
          </div>
        </div>
        <button
          onClick={() => onRemove(key)}
          className="text-muted-foreground hover:text-destructive"
          data-testid={`pos-remove-${item.product_id}`}
          title="Quitar del carrito"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Selector de unidad inline — solo cuando el producto tiene sub-unidades */}
      {hasSaleUnits && (
        <div
          className="mt-2 rounded-md border border-primary/30 bg-primary/5 dark:bg-primary/10 p-2"
          data-testid={`pos-cart-unit-selector-${item.product_id}`}
        >
          <div className="flex items-center gap-1.5 mb-1">
            <Layers className="w-3.5 h-3.5 text-primary" />
            <Label className="overline text-[10px] text-primary font-bold">
              Presentación de venta
            </Label>
          </div>
          <select
            value={currentUnit}
            onChange={(e) => onChangeUnit(key, e.target.value)}
            className="h-8 text-xs border border-border rounded px-2 bg-background w-full font-semibold focus:ring-2 focus:ring-primary/40 focus:outline-none"
            data-testid={`pos-unit-select-${item.product_id}`}
          >
            <option value="__BASE__">
              {(item.base_unit || "UND")} · {formatDOP(Number(item.base_price ?? item.list_price))} · Stock {item.base_stock ?? 0}
            </option>
            {(item.sale_units || [])
              .filter((u) => u.active !== false)
              .map((u) => {
                const ratio = Number(u.ratio) || 1;
                const availStock = Math.floor((item.base_stock ?? 0) * ratio * 100) / 100;
                return (
                  <option key={u.unit} value={u.unit}>
                    {u.unit} · {formatDOP(Number(u.price))} c/u · Stock {availStock}
                  </option>
                );
              })}
          </select>
        </div>
      )}

      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-1">
          <Button
            size="icon"
            variant="outline"
            className="h-7 w-7"
            onClick={() => onUpdateQty(key, item.quantity - 1)}
          >
            <Minus className="w-3 h-3" />
          </Button>
          <Input
            type="number"
            min={0}
            step={item.sale_unit || item.quantity % 1 !== 0 ? "0.01" : "1"}
            inputMode="decimal"
            value={qtyValue}
            onChange={(e) => onQtyDraftChange(key, e.target.value)}
            onBlur={() => onQtyCommit(key)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.target.blur();
              }
            }}
            className="h-7 w-20 text-center font-mono tabular-nums"
            data-testid={`pos-qty-input-${item.product_id}`}
          />
          <Button
            size="icon"
            variant="outline"
            className="h-7 w-7"
            onClick={() => onUpdateQty(key, item.quantity + 1)}
          >
            <Plus className="w-3 h-3" />
          </Button>
        </div>
        <div className="data-cell font-bold text-sm">
          {formatDOP(item.quantity * item.price)}
        </div>
      </div>

      {canDiscount && (
        <div className="flex items-center justify-between mt-2 gap-2">
          <Label className="overline text-[10px]">Precio</Label>
          <div className="flex items-center gap-1">
            <Input
              type="number"
              step="0.01"
              min={0}
              value={priceValue}
              onChange={(e) => onPriceDraftChange(key, e.target.value)}
              onBlur={() => onPriceCommit(key)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.target.blur();
                }
              }}
              className={`h-7 w-24 text-right font-mono tabular-nums ${
                priceEdited ? "text-primary border-primary/60" : ""
              }`}
              data-testid={`pos-price-input-${item.product_id}`}
            />
            {priceEdited && (
              <span
                className="text-[10px] text-muted-foreground line-through"
                title="Precio de lista"
              >
                {formatDOP(item.list_price)}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
